import buffer from '@turf/buffer'
import center from '@turf/center'
import flip from '@turf/flip'
import { featureCollection, point } from '@turf/helpers'
import { coordEach } from '@turf/meta'
import { toWgs84 } from '@turf/projection'
import union from '@turf/union'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import unset from 'lodash/unset'
import OLFeature from 'ol/Feature'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import OLFormatWKT from 'ol/format/WKT'

import type { Feature, FeatureCollection as FeatureCollectionT } from '@turf/helpers'
import type { FeatureCollection } from 'geojson'
import type OLRenderFeature from 'ol/render/Feature'
import type OLSourceVector from 'ol/source/Vector'

import PoziAPIData from 'api/data'
import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import GetTitleId from 'config/GetTitleId'
import { WHATS_HERE_LAYER_TITLEID } from 'drawer/tabs/info/WhatsHereUtils'
import { selectStyleCurrentTarget } from 'drawer/tabs/tools/select'
import PoziMap from 'map/map'
import PoziNotifications from 'utils/notifications'

export const CURRENT_TARGET_LAYER_TITLE = 'pozicurrenttarget'
export const CURRENT_TARGET_LAYER_TITLEID = GetTitleId(CURRENT_TARGET_LAYER_TITLE) as string

/**
 * The Pozi config object for the currentTarget layer.
 */
export const currentTargetLayerConfig = {
  title: CURRENT_TARGET_LAYER_TITLE,
  titleid: CURRENT_TARGET_LAYER_TITLEID,
  group: 'PoziGeneral',
  type: 'GeoJSON',
  visible: true,
  queryable: false,
  showInLayerControl: false,
  olStyle: selectStyleCurrentTarget,
  config: {
    spatial: {
      loader: 'none'
    }
  }
}

/**
 * Pozi Target module.
 * Functions to act on the target geometry.
 */

function isPoint () {
  const target = Model.get('app.map.target.featurecollection', null)
  if (isNil(target)) {
    return false
  }
  const features = get(target, 'features', [])
  if (features.length !== 1) {
    return false
  }
  const type = get(target, 'features[0].geometry.type', '')
  if (type !== 'Point') {
    return false
  }
  return true
}

export function targetGetCenter () {
  const target = Model.get('app.map.target.featurecollection', null)
  if (isNil(target)) {
    return null
  }
  if (isPoint()) {
    const coordinates = get(target, 'features[0].geometry.coordinates', null)
    return point(coordinates)
  }
  return center(target)
}

export function targetGet () {
  const target = Model.get('app.map.target.featurecollection', null)

  console.debug('targetGet', target)

  return target
}

/**
 * Is a target set?
 */
export function targetIsset () {
  const featureCollectionT = targetGet()
  if (isNil(featureCollectionT)) {
    return false
  }
  return true
}

/**
 * Unset a target
 */
export function targetUnset () {
  const bControlKey = Model.get('app.map.click.controlkey', false)
  if (!bControlKey) {
    Model.unset('app.map.target.featurecollection')
  }
}

export function targetSetOLFeature (olFeature: OLRenderFeature, layerConf: ConfigLayerOptions) {
  console.debug('targetSetOLFeature', layerConf?.titleid, olFeature, layerConf)

  Model.unset('app.state.target') // Clear any old targets.
  Model.unset('app.state.click') // Clear any old clicks.
  Model.unset('app.state.clickx')
  Model.unset('app.state.clicky')
  Model.unset('app.state.feature') // Clear any old features.

  const turfFeature = PoziUtils.convertOLFeatureToTurfFeature(olFeature)

  if (!isNil(layerConf)) {
    console.log('SelectVectorFeature map feature found. Reflect it in the URL')
    if (layerConf.titleid === WHATS_HERE_LAYER_TITLEID) {
      setFeatureStateToWhatsHere(turfFeature as Feature)
    } else {
      setFeatureStateToValue(layerConf, olFeature)
    }
  }
  HistoryUpdate()
  Model.set('app.map.target.ol.feature', olFeature)

  if (turfFeature) {
    let featureCollectionT = featureCollection([turfFeature])
    featureCollectionT = toWgs84(featureCollectionT)
    // console.log('selectVectorFeature: Removing properties and id because it causes issues with turf?')
    // unset(featureCollectionT, 'features[0].properties')
    // unset(featureCollectionT, 'features[0].id')
    targetSet(featureCollectionT, { mapclick: false })
  }
}

function setFeatureStateToWhatsHere (turfFeature: Feature) {
  console.debug('setFeatureStateToWhatsHere', turfFeature)

  if (isNil(turfFeature)) {
    console.log('Invalid turf feature for whatshere.')
  } else {
    const wkt = turfFeature?.properties?.$wkt ?? null
    if (isNil(wkt)) {
      console.log('Invalid turf feature for whatshere - no $wkt property')
    } else {
      // If the value represents a single point, shorten it to just the coordinates, otherwise use the whole wkt
      // The counterpart code can be found in parseHash in history.jsx
      const singlePointValue = wkt.match(/^POINT\((.+)\)$/)?.[1]

      const featureIdValue = singlePointValue || wkt

      Model.setState('feature', WHATS_HERE_LAYER_TITLEID, featureIdValue)
    }
  }
}

function setFeatureStateToValue (layerConf: ConfigLayerOptions, olFeature: OLRenderFeature) {
  console.debug('setFeatureStateToValue', layerConf, olFeature)

  const propId = layerConf.config?.spatial?.id
  if (!propId) {
    console.log('Unable to find a valid propId in config for selected feature.')
  } else {
    const value = PoziUtils.getFeaturePropertyValueCaseInsensitive(olFeature, propId)
    if (isNil(value)) {
      console.log('Unable to find a valid value for selected feature.')
    } else {
      Model.setState('feature', layerConf.titleid, value)
    }
  }
}

export function targetSet (featureCollectionT: FeatureCollection | FeatureCollectionT, options: Record<string, any> = {}) {
  console.log('PoziTarget set', featureCollectionT, options)
  if (options?.mapclick) {
    Model.set('app.map.target.mapclick', true)
  } else {
    Model.unset('app.map.target.mapclick')
  }
  Model.set('app.map.target.featurecollection', featureCollectionT)
}
/**
 * Selected Tab Config
 */
export function targetSetSelectedTabElementByTabID (tabID: string) {
  const tabElement = document.getElementById(tabID)
  if (!tabElement) {
    console.log('Unable to find tab element with tabID', tabID)
    Model.unset('app.map.target.selection.tab.element')
    return
  }
  Model.set('app.map.target.selection.tab.element', tabElement)
}

/**
 * Used in WFS requests to filter results to specific geography.
 */
export function targetGetCQLFilterQuery (geometryname: string) {
  let featureCollectionT: FeatureCollectionT = targetGet()

  if (isNil(featureCollectionT)) {
    console.log('No target set.')
    return ''
  }

  const fcIsMapClick = Model.get('app.map.target.mapclick', false)

  if (fcIsMapClick) {
    return targetGenerateQuery(featureCollectionT, geometryname)
  } else {
    if (featureCollectionT.features.length > 1) {
      const features = featureCollectionT.features ?? []

      // In turfjs v7+ we should be able to run union on a feature collection.
      // Until then, we do it his way.
      //
      // @ts-expect-error
      // eslint-disable-next-line prefer-spread
      const unionT = union.apply(null, features)

      // @ts-expect-error
      featureCollectionT = featureCollection([unionT])
    }

    featureCollectionT = buffer(featureCollectionT, -0.0001, {
      units: 'kilometers',
      steps: 12
    })

    // Truncating seems to cause problems with GeoServer.
    // Our best theory is that truncate creates duplicate points, which should be fine, but causes geoserver to return wierd results.
    // Like it thinks it is a donut stucture or something.
    // Setting the precision seems to help, but still seeing this occassionally.
    // Note that by using simplify after it resolves the problem, but then simplify makes the coords long again! DOH!
    /*
      featureCollectionT = truncate(featureCollectionT, {'precision': 6})
      featureCollectionT = simplify(featureCollectionT, {
        'tolerance': 0.000005,
        'highQuality': false,
        'mutate': true
      }) */

    if (isNil(featureCollectionT)) {
      console.error('getCQLPolygonFilter: Unable to get buffered region.')
      return ''
    }
    return targetGenerateQuery(featureCollectionT, geometryname)
  }
}

// ============================================================
// Private Functions
// ============================================================

export function targetGenerateQuery (featureCollectionT: FeatureCollectionT, geometryname: string) {
  console.log('Note: flip is required for DELWP GeoServer. Maybe breaks other data sources? Might need to be a config option.')
  featureCollectionT.features.forEach(function (feature) {
    unset(feature, 'properties')
  })
  const flipped = flip(featureCollectionT)
  const olGeoJSON = new OLFormatGeoJSON()
  const olFeatures = olGeoJSON.readFeatures(flipped)
  const olFormatWKT = new OLFormatWKT()
  const wktString = olFormatWKT.writeFeatures(olFeatures)
  return 'INTERSECTS(' + geometryname + ',' + wktString + ')'
}

/**
 * Gets the property info and sets it as the current target.
 * Note: This was a feature request that would return all the features intersecting a
 *       property boundary. E.g. Returns the Planning zones and overlays even if the click point is not on an overlay.
 */
// Note: this function appears not to used

export function targetSetGeometryTargetWithURL (url: string) {
  return new Promise(function (resolve, reject) {
    if (!url || url === 'vicproperty') {
      targetSetToClickLocation()
      const cqlfilter = targetGetCQLFilterQuery('geom')
      const maxFeatures = Model.get('config.settings.maxfeatures', 100) // This is set in templates.json
      // Do not use maxFeatures=1 because this can return a random property that is not under the pointer.
      // Strategy is to get a bunch of properties and sort by size below.
      url =
        'https://opendata.maps.vic.gov.au/geoserver/ows?SERVICE=WFS&VERSION=1.1.0&REQUEST=GetFeature&srsName=EPSG%3A4326' +
        '&typeNames=v_property_mp_address&maxFeatures=' +
        maxFeatures +
        '&outputFormat=application%2Fjson&CQL_FILTER=' +
        cqlfilter +
        '%20AND%20prop_property_type%20%3D%20%27O%27&propertyName=prop_pfi,prop_lga_code,prop_propnum,prop_multi_assessment,prop_status'
    }

    // console.log(url)
    PoziAPIData().ajax({
      dataType: 'json',
      method: 'POST',
      timeout: 30000,
      cache: true,
      url,
      error: function (err: Error) {
        console.log('Unable to set geometry from property geojson, setting to current map selection instead.', err)
        targetSetToClickLocation()
        return resolve(true)
      },
      success: function (featureCollectionT: FeatureCollectionT) {
        let err = ''
        const bValidFeatureCollection = get(featureCollectionT, 'features[0].geometry', false)
        if (bValidFeatureCollection === false) {
          err = 'Invalid feature collection. Setting target to current map selection instead.'
          console.error(err)
          targetSetToClickLocation()
          return resolve(true)
        }
        let coordsCount = 0
        coordEach(featureCollectionT, function (currentCoord, coordIndex, featureIndex, multiFeatureIndex, geometryIndex) {
          coordsCount++
        })
        if (coordsCount > 1000) {
          err = 'Feature collection contains too many features. Setting target to current map selection instead.' + coordsCount
          console.error(err)
          targetSetToClickLocation()
          PoziNotifications().warning('Feature too complex. Query simplified based on point selection.')
          return resolve(true)
        }
        PoziUtils.sortFeatureCollectionByArea(featureCollectionT)
        targetSet(featureCollectionT)
        return resolve(true)
      }
    })
  })
}

/**
 * Sets the current target to the map point.
 * Essentially a reset to default selector.
 */
export function targetSetToClickLocation () {
  console.log('PoziTabs: Set target featurecollection to click location...')
  const markerPosition = PoziMap().getMarkerPosition()
  if (isNil(markerPosition)) {
    console.log('No marker position... unset target.')
    targetUnset()
    return false
  }
  const coords = get(markerPosition, 'geometry.coordinates', null)
  if (!coords) {
    console.error('Unable to get coordinates from click target.')
    targetUnset()
    return false
  }
  const targetPoint = point(coords, { mapclick: true })
  const featureCollectionT = featureCollection([targetPoint])
  targetSet(featureCollectionT, { mapclick: true })
  return true
}

/**
 * Clear all geometries from the currentTarget layer
 */
export function currentTargetLayerClear () {
  const olCurrentTargetLayerSource = PoziUtils.getOpenLayersObjectFromTitleID(CURRENT_TARGET_LAYER_TITLEID)?.getSource() as OLSourceVector

  console.debug('currentTargetLayerClear', olCurrentTargetLayerSource)

  if (olCurrentTargetLayerSource) {
    olCurrentTargetLayerSource.clear()
  } else {
    console.debug('currentTargetLayerClear - no currentTarget layer or source. This should not happen.')
  }
}

/**
 * Show the feature geometry on the currentTarget layer
 *
 */
export function currentTargetLayerShowGeom (fc: FeatureCollection | Feature) {
  console.debug('currentTargetLayerShowGeom', fc)

  const sourceCurrentTarget = PoziUtils.getOpenLayersObjectFromTitleID(CURRENT_TARGET_LAYER_TITLEID)?.getSource() as OLSourceVector
  if (isNil(sourceCurrentTarget)) {
    return
  }
  if (isNil(fc)) {
    return
  }

  let fcHasGeom = false

  // Simple way to check if feature (collection) has any geometries
  if (fc.type === 'FeatureCollection') {
    const features = fc.features || []
    fcHasGeom = features.some(feature => feature.geometry)
  } else {
    fcHasGeom = !!fc.geometry
  }

  // console.debug('CurrentTargetUtils - currentTargetShowGeom - feature collection has geom?', fcHasGeom)

  // Remove the currentTarget features
  sourceCurrentTarget.clear(true)

  if (!fcHasGeom) {
    return
  }

  const olFeatureArr = PoziUtils.convertGeoJSONFeatureCollectionToOLFeatureArray(fc as FeatureCollectionT) || []
  if (!olFeatureArr?.length) {
    return
  }

  // console.debug('currentTargetLayerShowGeom', fc, [...olFeatureArr])

  // Strip features of all attributes to avoid them having interfering styles
  const olFeatureArrClean = olFeatureArr.map(feature => {
    const cleanFeature = new OLFeature()
    cleanFeature.setGeometry(feature.getGeometry())
    cleanFeature.setStyle()
    return cleanFeature
  })

  sourceCurrentTarget.addFeatures(olFeatureArrClean)
  // sourceCurrentTarget.refresh()
}
